import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { syncSearchParams } from '@/lib/urlSync';
import { getCached, swrFetch, invalidatePrefix } from '@/lib/dataCache';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { prospectsAPI, pipelineAPI, usersAPI, teamsV2API } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HashtagChips } from '@/components/prospect/HashtagChips';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Phone, MapPin, Plus, GripVertical, Building2, UserCircle, Zap, Hammer, Users, Filter, ChevronRight, XCircle, AlertTriangle, Loader2, Clock } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';

const CLASS_LABELS = {
    agri: 'Agriculture',
    tertiaire: 'Tertiaire',
    renovation: 'Rénovation'
};

const CLASS_COLORS = {
    agri: 'bg-green-100 text-green-700',
    tertiaire: 'bg-blue-100 text-blue-700',
    renovation: 'bg-orange-100 text-orange-700'
};

// ProspectCard component
const ProspectCard = ({ prospect, provided, snapshot, onReassign, canReassign, onViewDetail, onNextStage, hasNextStage, pipelineType }) => {
    // Déterminer le responsable du dossier (apporteur ou VP si direct)
    const responsable = prospect.dossier_apporteur_name || prospect.dossier_vp_name || null;
    const isVpDirect = prospect.is_vp_direct || (!prospect.dossier_apporteur_id && prospect.dossier_vp_id);
    
    // Raison de perte
    const lossReason = prospect.ep_loss_reason || prospect.ch_loss_reason || null;
    const isLost = prospect.easy_power_stage === 'ep_perdu' || prospect.chantier_stage === 'ch_perdu';

    // Temps dans l'étape + retard
    const changedAt = pipelineType ? prospect[`${pipelineType}_stage_changed_at`] : null;
    const daysInStage = changedAt ? Math.floor((Date.now() - new Date(changedAt).getTime()) / 86400000) : null;
    const daysInactive = prospect.updated_at ? Math.floor((Date.now() - new Date(prospect.updated_at).getTime()) / 86400000) : null;
    const FINAL = ['ep_gagne', 'ep_questionnaire_chantier', 'ep_perdu', 'ch_valide', 'ch_perdu', 'ch_paiement_effectue'];
    const curStage = pipelineType ? prospect[`${pipelineType}_stage`] : null;
    const isOverdue = !isLost && curStage && !FINAL.includes(curStage) && daysInactive != null && daysInactive >= 10;
    
    return (
        <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={cn(
                "pipeline-card bg-white rounded-lg p-3 shadow-sm border border-slate-100",
                "hover:shadow-md cursor-pointer",
                isLost && "border-red-200 bg-red-50/30",
                snapshot.isDragging && "dragging shadow-xl rotate-2"
            )}
            onClick={() => onViewDetail(prospect.id)}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    {prospect.company_name && (
                        <p className="text-xs text-slate-500 flex items-center gap-1 mb-1 truncate">
                            <Building2 className="w-3 h-3 flex-shrink-0" />
                            <span className="hover:text-blue-600 hover:underline cursor-pointer">{prospect.company_name}</span>
                        </p>
                    )}
                    <p 
                        className="font-medium text-slate-900 text-sm truncate hover:text-blue-600 hover:underline cursor-pointer"
                        data-testid={`prospect-name-${prospect.id}`}
                    >
                        {prospect.first_name} {prospect.last_name}
                    </p>
                    {prospect.prospect_class && (
                        <Badge className={cn("mt-1 text-xs", CLASS_COLORS[prospect.prospect_class])}>
                            {CLASS_LABELS[prospect.prospect_class]}
                        </Badge>
                    )}
                    <HashtagChips
                        ids={pipelineType === 'chantier' ? prospect.hashtags_chantier : prospect.hashtags_easy_power}
                        max={2}
                        className="mt-1"
                    />
                </div>
                <div
                    {...provided.dragHandleProps}
                    className="p-1 hover:bg-slate-100 rounded"
                    onClick={(e) => e.stopPropagation()}
                >
                    <GripVertical className="w-4 h-4 text-slate-400" />
                </div>
            </div>
            
            {/* Responsable et Équipe */}
            <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                {!isLost && (daysInStage != null || isOverdue) && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {daysInStage != null && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1 text-slate-500" data-testid={`days-in-stage-${prospect.id}`}>
                                <Clock className="w-2.5 h-2.5" /> {daysInStage}j dans l'étape
                            </Badge>
                        )}
                        {isOverdue && (
                            <Badge className="text-[10px] px-1.5 py-0 gap-1 bg-amber-100 text-amber-700 hover:opacity-90" data-testid={`pipeline-overdue-${prospect.id}`}>
                                <AlertTriangle className="w-2.5 h-2.5" /> Retard {daysInactive - 10}j
                            </Badge>
                        )}
                    </div>
                )}
                {responsable && (
                    <div className="flex items-center gap-2 text-xs">
                        <UserCircle className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span className="text-slate-600 truncate" title={responsable}>
                            {responsable}
                        </span>
                        {isVpDirect && (
                            <Badge className="text-[10px] px-1 py-0 bg-purple-100 text-purple-700">VP</Badge>
                        )}
                    </div>
                )}
                {prospect.team_name && (
                    <div className="flex items-center gap-2 text-xs">
                        <Users className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span className="text-slate-500 truncate" title={prospect.team_name}>
                            {prospect.team_name}
                        </span>
                    </div>
                )}
            </div>
            
            <div className="mt-2 space-y-1">
                {prospect.phone && (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Phone className="w-3 h-3" />
                        <span className="truncate">{prospect.phone}</span>
                    </div>
                )}
                {prospect.city && (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{prospect.city}</span>
                    </div>
                )}
            </div>

            {/* Raison de perte */}
            {isLost && lossReason && (
                <div className="mt-2 px-2 py-1.5 bg-red-50 border border-red-200 rounded text-xs text-red-600" data-testid={`loss-reason-${prospect.id}`}>
                    <div className="flex items-center gap-1 font-medium">
                        <XCircle className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{lossReason}</span>
                    </div>
                </div>
            )}

            <div className="mt-2 flex gap-1">
                {hasNextStage && (
                    <Button
                        variant="default"
                        size="sm"
                        className="flex-1 text-xs h-7 bg-blue-600 hover:bg-blue-700"
                        onClick={(e) => {
                            e.stopPropagation();
                            onNextStage(prospect);
                        }}
                        data-testid={`next-stage-${prospect.id}`}
                    >
                        <ChevronRight className="w-3 h-3 mr-1" />
                        Étape suivante
                    </Button>
                )}
                {canReassign && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className={cn("text-xs h-7", hasNextStage ? "px-2" : "w-full")}
                        onClick={(e) => {
                            e.stopPropagation();
                            onReassign(prospect);
                        }}
                    >
                        <UserCircle className="w-3 h-3" />
                        {!hasNextStage && <span className="ml-1">Réassigner</span>}
                    </Button>
                )}
            </div>
        </div>
    );
};

// Single Pipeline Column
const PipelineColumn = ({ stage, stages, prospects, onDragEnd, onReassign, canReassign, onViewDetail, onNextStage }) => {
    const stageProspects = prospects.filter(p => {
        // Pour Courtage
        if (stage.id.startsWith('ep_')) {
            return p.easy_power_stage === stage.id;
        }
        // Pour Travaux d’optimisation
        if (stage.id.startsWith('ch_')) {
            return p.chantier_stage === stage.id;
        }
        return false;
    });
    
    // Check if there's a next stage
    const stageList = stage.id.startsWith('ep_') ? stages.easy_power : stages.chantier;
    const currentIndex = stageList?.findIndex(s => s.id === stage.id) ?? -1;
    const hasNextStage = currentIndex >= 0 && currentIndex < (stageList?.length ?? 0) - 1;

    return (
        <div className="flex-shrink-0 w-56 sm:w-64 flex flex-col">
            <div className="rounded-xl overflow-hidden bg-slate-50 border border-slate-200 flex flex-col flex-1 min-h-0">
                {/* Header */}
                <div 
                    className="px-3 py-2 flex items-center justify-between shrink-0"
                    style={{ backgroundColor: stage.color }}
                >
                    <h3 className="font-medium text-white text-sm truncate" title={stage.name}>
                        {stage.name}
                    </h3>
                    <Badge variant="secondary" className="bg-white/20 text-white text-xs">
                        {stageProspects.length}
                    </Badge>
                </div>

                {/* Droppable Area */}
                <Droppable droppableId={stage.id}>
                    {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            data-col-scroll
                            className={cn(
                                "p-2 space-y-2 flex-1 min-h-[120px] overflow-y-auto transition-colors",
                                snapshot.isDraggingOver && "bg-white/50"
                            )}
                        >
                            {stageProspects.map((prospect, index) => (
                                <Draggable
                                    key={prospect.id}
                                    draggableId={prospect.id}
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <ProspectCard
                                            prospect={prospect}
                                            provided={provided}
                                            snapshot={snapshot}
                                            onReassign={onReassign}
                                            canReassign={canReassign}
                                            onViewDetail={onViewDetail}
                                            onNextStage={onNextStage}
                                            hasNextStage={hasNextStage}
                                            pipelineType={stage.id.startsWith('ep_') ? 'easy_power' : 'chantier'}
                                        />
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </div>
        </div>
    );
};

// Conteneur du board kanban : hauteur bornée à l'écran (la barre de défilement horizontale
// reste donc toujours visible quel que soit le nombre de cartes), défilement vertical par
// colonne, + glisser-pour-déplacer (drag-to-pan) et molette → horizontale pour une nav fluide.
const PipelineBoard = ({ children }) => {
    const ref = useRef(null);
    const drag = useRef({ active: false, startX: 0, scrollLeft: 0 });

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const onWheel = (e) => {
            if (e.deltaY === 0 || e.shiftKey) return;
            const col = e.target.closest('[data-col-scroll]');
            const colScrollable = col && col.scrollHeight > col.clientHeight + 1;
            // Si la colonne survolée ne peut pas défiler verticalement, la molette défile le board horizontalement
            if (!colScrollable) {
                el.scrollLeft += e.deltaY;
                e.preventDefault();
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    const onPointerDown = (e) => {
        if (e.button !== 0) return;
        // Ne pas paner si on agrippe une carte ou un élément interactif (préserve clic & drag&drop)
        if (e.target.closest('.pipeline-card, button, a, input, textarea, select, [role="button"]')) return;
        const el = ref.current;
        drag.current = { active: true, startX: e.clientX, scrollLeft: el.scrollLeft };
        el.classList.add('panning');
    };
    const onPointerMove = (e) => {
        if (!drag.current.active) return;
        ref.current.scrollLeft = drag.current.scrollLeft - (e.clientX - drag.current.startX);
    };
    const endPan = () => {
        if (!drag.current.active) return;
        drag.current.active = false;
        ref.current?.classList.remove('panning');
    };

    return (
        <div
            ref={ref}
            data-testid="pipeline-board"
            className="flex gap-3 overflow-x-auto overflow-y-hidden pb-3 -mx-4 px-4 pipeline-scrollable pipeline-board max-h-[calc(100vh-240px)]"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPan}
            onPointerLeave={endPan}
        >
            {children}
        </div>
    );
};

export default function Pipeline() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [prospects, setProspects] = useState([]);
    const [stages, setStages] = useState({ easy_power: [], chantier: [] });
    const [loading, setLoading] = useState(true);
    const [teamMembers, setTeamMembers] = useState([]);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'easy_power');
    
    // Team filter
    const [teams, setTeams] = useState([]);
    const [selectedTeam, setSelectedTeam] = useState(searchParams.get('team') || 'all');

    // Synchroniser onglet + équipe avec l'URL (préservation du contexte au retour).
    useEffect(() => {
        const params = new URLSearchParams();
        if (activeTab !== 'easy_power') params.set('tab', activeTab);
        if (selectedTeam !== 'all') params.set('team', selectedTeam);
        syncSearchParams(setSearchParams, searchParams, params);
    }, [activeTab, selectedTeam]); // eslint-disable-line react-hooks/exhaustive-deps
    
    // Reassignment dialog
    const [reassignDialog, setReassignDialog] = useState({ open: false, prospect: null });
    const [selectedMember, setSelectedMember] = useState('');
    
    // Loss reason dialog (for drag-and-drop to Perdu)
    const [lossDialog, setLossDialog] = useState({ open: false, prospectId: null, newStage: null, pipelineType: null });
    const [lossReason, setLossReason] = useState('');
    const [lossComment, setLossComment] = useState('');
    const [savingLoss, setSavingLoss] = useState(false);
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async (force = false) => {
        const key = `pipeline:${user?.id}:${user?.role}`;
        if (force) {
            invalidatePrefix('pipeline');
            invalidatePrefix('prospects');
            invalidatePrefix('dashboard');
            invalidatePrefix('pilotage');
        }
        const cached = !force && getCached(key);
        if (cached) {
            setProspects(cached.prospects);
            setStages(cached.stages);
            setTeams(cached.teams);
            if (cached.teamMembers) setTeamMembers(cached.teamMembers);
            setLoading(false);
        } else if (!force) {
            setLoading(true);
        }
        try {
            const data = await swrFetch(key, async () => {
                const promises = [
                    prospectsAPI.getAll({ page_size: 100000 }, { timeout: 60000 }),
                    pipelineAPI.getStages(),
                    teamsV2API.getAll(),
                    user?.role === 'vp' ? usersAPI.getVPTeam(user.id) : Promise.resolve(null),
                ];
                const results = await Promise.all(promises);
                return {
                    prospects: results[0].data,
                    stages: results[1].data,
                    teams: results[2].data || [],
                    teamMembers: results[3] ? results[3].data : undefined,
                };
            }, { force });
            setProspects(data.prospects);
            setStages(data.stages);
            setTeams(data.teams);
            if (data.teamMembers) setTeamMembers(data.teamMembers);
        } catch (error) {
            if (!cached) {
                console.error('Error loading pipeline:', error);
                toast.error('Erreur lors du chargement');
            }
        } finally {
            setLoading(false);
        }
    };
    
    // Filter prospects by team
    const getFilteredProspects = () => {
        if (selectedTeam === 'all') {
            return prospects;
        }
        // Support both team_id (single) and team_ids (array) for backwards compatibility
        return prospects.filter(p => {
            if (p.team_ids && Array.isArray(p.team_ids)) {
                return p.team_ids.includes(selectedTeam);
            }
            return p.team_id === selectedTeam;
        });
    };
    
    const filteredProspects = getFilteredProspects();

    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const { draggableId, destination } = result;
        const newStage = destination.droppableId;

        // INTERCEPTER le déplacement vers "Perdu" - ouvrir le dialog raison
        if (newStage === 'ep_perdu' || newStage === 'ch_perdu') {
            const pipelineType = newStage.startsWith('ep_') ? 'easy_power' : 'chantier';
            setLossDialog({ open: true, prospectId: draggableId, newStage, pipelineType });
            setLossReason('');
            setLossComment('');
            return;
        }

        // Optimistic update
        setProspects(prev => prev.map(p => {
            if (p.id === draggableId) {
                if (newStage.startsWith('ep_')) {
                    return { ...p, easy_power_stage: newStage };
                } else if (newStage.startsWith('ch_')) {
                    return { ...p, chantier_stage: newStage };
                }
            }
            return p;
        }));

        try {
            // Determine which pipeline endpoint to call
            if (newStage.startsWith('ep_')) {
                await api.put(`/prospects/${draggableId}/easy-power-stage?stage=${newStage}`);
            } else if (newStage.startsWith('ch_')) {
                await api.put(`/prospects/${draggableId}/chantier-stage?stage=${newStage}`);
            }
            
            const stageName = [...stages.easy_power, ...stages.chantier].find(s => s.id === newStage)?.name || newStage;
            toast.success(`Déplacé vers "${stageName}"`);
            invalidatePrefix('prospects'); invalidatePrefix('dashboard'); invalidatePrefix('pilotage'); invalidatePrefix('pipeline');
        } catch (error) {
            loadData(true); // Revert
            toast.error('Erreur lors de la mise à jour');
        }
    };

    // Confirmer la perte avec raison depuis le dialog pipeline
    const LOSS_REASONS_EP = [
        { id: 'prix', label: 'Prix trop élevé' },
        { id: 'concurrent', label: 'Parti chez un concurrent' },
        { id: 'non_interesse', label: 'Plus intéressé' },
        { id: 'budget', label: 'Pas de budget' },
        { id: 'timing', label: 'Mauvais timing' },
        { id: 'contact_perdu', label: 'Contact perdu / Injoignable' },
        { id: 'changement_interlocuteur', label: "Changement d'interlocuteur" },
        { id: 'fournisseur_actuel', label: 'Reste avec fournisseur actuel' },
        { id: 'autre', label: 'Autre (préciser)' },
    ];
    const LOSS_REASONS_CH = [
        { id: 'prix', label: 'Devis trop élevé' },
        { id: 'concurrent', label: 'Parti chez un concurrent' },
        { id: 'non_interesse', label: 'Plus intéressé' },
        { id: 'budget', label: 'Pas de budget / Financement refusé' },
        { id: 'timing', label: 'Report du projet' },
        { id: 'contact_perdu', label: 'Contact perdu / Injoignable' },
        { id: 'refus_subvention', label: 'Subvention refusée' },
        { id: 'conditions_techniques', label: 'Conditions techniques non remplies' },
        { id: 'autre', label: 'Autre (préciser)' },
    ];

    const handleConfirmLoss = async () => {
        if (!lossReason) {
            toast.error('Veuillez sélectionner une raison');
            return;
        }
        if (!lossComment.trim()) {
            toast.error('Veuillez expliquer pourquoi le dossier est perdu');
            return;
        }
        
        setSavingLoss(true);
        try {
            const reasons = lossDialog.pipelineType === 'easy_power' ? LOSS_REASONS_EP : LOSS_REASONS_CH;
            const reasonLabel = reasons.find(r => r.id === lossReason)?.label || lossReason;
            const fullComment = `Affaire perdue - ${reasonLabel}: ${lossComment.trim()}`;
            
            await prospectsAPI.changeStage(
                lossDialog.prospectId, 
                lossDialog.pipelineType, 
                lossDialog.newStage, 
                fullComment, 
                reasonLabel
            );
            
            toast.success('Dossier marqué comme perdu');
            setLossDialog({ open: false, prospectId: null, newStage: null, pipelineType: null });
            loadData(true);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSavingLoss(false);
        }
    };

    const handleReassign = async () => {
        if (!selectedMember || !reassignDialog.prospect) return;
        
        try {
            await prospectsAPI.update(reassignDialog.prospect.id, { assigned_to: selectedMember });
            toast.success('Dossier réassigné');
            setReassignDialog({ open: false, prospect: null });
            setSelectedMember('');
            loadData(true);
        } catch (error) {
            toast.error('Erreur lors de la réassignation');
        }
    };

    const handleNextStage = async (prospect) => {
        try {
            // Déterminer le pipeline actif selon l'onglet actuel
            const isEasyPowerTab = activeTab === 'easy_power';
            const currentStage = isEasyPowerTab ? prospect.easy_power_stage : prospect.chantier_stage;
            
            if (!currentStage) {
                toast.info('Pas d\'étape active sur ce pipeline');
                return;
            }
            
            // Utiliser les étapes du pipeline actif
            const stageList = isEasyPowerTab ? stages.easy_power : stages.chantier;
            
            // Find current index and next stage
            const currentIndex = stageList.findIndex(s => s.id === currentStage);
            if (currentIndex < 0 || currentIndex >= stageList.length - 1) {
                toast.info('Dernière étape atteinte');
                return;
            }
            
            const nextStage = stageList[currentIndex + 1];
            
            // Mise à jour optimiste IMMÉDIATE
            setProspects(prev => prev.map(p => {
                if (p.id === prospect.id) {
                    if (isEasyPowerTab) {
                        return { ...p, easy_power_stage: nextStage.id };
                    } else {
                        return { ...p, chantier_stage: nextStage.id };
                    }
                }
                return p;
            }));
            
            // API call en arrière-plan
            if (isEasyPowerTab) {
                await api.put(`/prospects/${prospect.id}/easy-power-stage?stage=${nextStage.id}`);
            } else {
                await api.put(`/prospects/${prospect.id}/chantier-stage?stage=${nextStage.id}`);
            }
            
            toast.success(`Avancé vers "${nextStage.name}"`);
            invalidatePrefix('prospects'); invalidatePrefix('dashboard'); invalidatePrefix('pilotage'); invalidatePrefix('pipeline');
        } catch (error) {
            loadData(true); // Revert on error
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const openReassignDialog = (prospect) => {
        setReassignDialog({ open: true, prospect });
    };

    // Filter prospects for Travaux d’optimisation pipeline (only those who have won Courtage or have chantier_stage set)
    // Also apply team filter
    const chantierProspects = filteredProspects.filter(p => 
        p.easy_power_stage === 'ep_gagne' || 
        p.easy_power_stage === 'ep_questionnaire_chantier' ||
        p.chantier_stage
    );

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <div className="flex gap-4 overflow-x-auto pb-4 pipeline-scrollable">
                        {[1, 2, 3, 4, 5].map(i => (
                            <Skeleton key={i} className="h-96 w-64 flex-shrink-0" />
                        ))}
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <h1 className="text-xl sm:text-2xl font-outfit font-semibold text-slate-900">
                                Pipeline
                            </h1>
                            <p className="text-slate-600 text-xs sm:text-sm mt-1">
                                Glissez-déposez pour avancer les dossiers
                            </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                        {/* Team Filter */}
                        {teams.length > 0 && (
                            <div className="flex items-center gap-2">
                                <Filter className="w-4 h-4 text-slate-500" />
                                <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                                    <SelectTrigger className="w-[180px]" data-testid="team-filter">
                                        <SelectValue placeholder="Filtrer par équipe" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">
                                            <span className="flex items-center gap-2">
                                                <Users className="w-4 h-4" />
                                                Toutes les équipes
                                            </span>
                                        </SelectItem>
                                        {teams.map((team) => (
                                            <SelectItem key={team.id} value={team.id}>
                                                <span className="flex items-center gap-2">
                                                    <span 
                                                        className="w-3 h-3 rounded-full" 
                                                        style={{ backgroundColor: team.color || '#3B82F6' }}
                                                    />
                                                    {team.name}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <Button
                            onClick={() => navigate('/prospects/new')}
                            className="bg-slate-900 hover:bg-slate-800"
                            data-testid="add-prospect-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Nouveau Prospect
                        </Button>
                    </div>
                </div>
                
                {/* Filter indicator */}
                {selectedTeam !== 'all' && (
                    <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-100 px-3 py-2 rounded-lg">
                        <Users className="w-4 h-4" />
                        Filtré par équipe : <strong>{teams.find(t => t.id === selectedTeam)?.name}</strong>
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 px-2 ml-2"
                            onClick={() => setSelectedTeam('all')}
                        >
                            Effacer
                        </Button>
                        <span className="text-slate-400">({filteredProspects.length} dossiers)</span>
                    </div>
                )}

                {/* Tabs for two pipelines */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full max-w-md grid-cols-2">
                        <TabsTrigger value="easy_power" className="flex items-center gap-2">
                            <Zap className="w-4 h-4" />
                            Courtage
                        </TabsTrigger>
                        <TabsTrigger value="chantier" className="flex items-center gap-2">
                            <Hammer className="w-4 h-4" />
                            Travaux d’optimisation
                        </TabsTrigger>
                    </TabsList>

                    {/* Courtage Pipeline */}
                    <TabsContent value="easy_power" className="mt-4">
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <PipelineBoard>
                                {stages.easy_power?.map((stage) => (
                                    <PipelineColumn
                                        key={stage.id}
                                        stage={stage}
                                        stages={stages}
                                        prospects={filteredProspects}
                                        onDragEnd={handleDragEnd}
                                        onReassign={openReassignDialog}
                                        canReassign={user?.role === 'vp' && teamMembers.length > 0}
                                        onViewDetail={(id) => navigate(`/prospects/${id}`)}
                                        onNextStage={handleNextStage}
                                    />
                                ))}
                            </PipelineBoard>
                        </DragDropContext>
                    </TabsContent>

                    {/* Travaux d’optimisation Pipeline */}
                    <TabsContent value="chantier" className="mt-4">
                        {chantierProspects.length === 0 ? (
                            <Card className="p-8 text-center">
                                <Hammer className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                <p className="text-slate-500">Aucun dossier en phase Travaux d’optimisation</p>
                                <p className="text-sm text-slate-400 mt-1">
                                    Les prospects apparaissent ici une fois le contrat Courtage gagné
                                </p>
                            </Card>
                        ) : (
                            <DragDropContext onDragEnd={handleDragEnd}>
                                <PipelineBoard>
                                    {stages.chantier?.map((stage) => (
                                        <PipelineColumn
                                            key={stage.id}
                                            stage={stage}
                                            stages={stages}
                                            prospects={chantierProspects}
                                            onDragEnd={handleDragEnd}
                                            onReassign={openReassignDialog}
                                            canReassign={user?.role === 'vp' && teamMembers.length > 0}
                                            onViewDetail={(id) => navigate(`/prospects/${id}`)}
                                            onNextStage={handleNextStage}
                                        />
                                    ))}
                                </PipelineBoard>
                            </DragDropContext>
                        )}
                    </TabsContent>
                </Tabs>
            </div>

            {/* Reassignment Dialog */}
            <Dialog open={reassignDialog.open} onOpenChange={(open) => setReassignDialog({ open, prospect: reassignDialog.prospect })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Réassigner le dossier</DialogTitle>
                        <DialogDescription>
                            {reassignDialog.prospect && (
                                <>Attribuer <strong>{reassignDialog.prospect.first_name} {reassignDialog.prospect.last_name}</strong> à un membre de votre équipe</>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Select value={selectedMember} onValueChange={setSelectedMember}>
                            <SelectTrigger>
                                <SelectValue placeholder="Sélectionner un membre" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={user?.id}>Moi-même</SelectItem>
                                {teamMembers.map((member) => (
                                    <SelectItem key={member.id} value={member.id}>
                                        {member.first_name} {member.last_name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReassignDialog({ open: false, prospect: null })}>
                            Annuler
                        </Button>
                        <Button onClick={handleReassign} disabled={!selectedMember}>
                            Réassigner
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Loss Reason Dialog (drag-and-drop to Perdu) */}
            <Dialog open={lossDialog.open} onOpenChange={(open) => {
                if (!open) setLossDialog({ open: false, prospectId: null, newStage: null, pipelineType: null });
            }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Marquer comme perdu
                        </DialogTitle>
                        <DialogDescription>
                            Veuillez indiquer la raison de la perte de ce dossier.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">
                                Raison de la perte <span className="text-red-500">*</span>
                            </Label>
                            <Select value={lossReason} onValueChange={setLossReason}>
                                <SelectTrigger data-testid="pipeline-loss-reason-select">
                                    <SelectValue placeholder="Sélectionnez une raison..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {(lossDialog.pipelineType === 'easy_power' ? LOSS_REASONS_EP : LOSS_REASONS_CH).map((r) => (
                                        <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">
                                Expliquez pourquoi <span className="text-red-500">*</span>
                            </Label>
                            <Textarea
                                placeholder="Décrivez la situation et la raison de la perte..."
                                value={lossComment}
                                onChange={(e) => setLossComment(e.target.value)}
                                rows={3}
                                data-testid="pipeline-loss-comment"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setLossDialog({ open: false, prospectId: null, newStage: null, pipelineType: null })} disabled={savingLoss}>
                            Annuler
                        </Button>
                        <Button 
                            variant="destructive" 
                            onClick={handleConfirmLoss} 
                            disabled={savingLoss || !lossReason || !lossComment.trim()}
                            data-testid="pipeline-confirm-loss-btn"
                        >
                            {savingLoss && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Confirmer la perte
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
